import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import type { FindStoreResultViewModel } from 'autogen/swagger/all';
import {
    useSearchStoreForProductByAddressQuery,
    useSearchStoreForProductByCoordinatesQuery
} from 'autogen/swagger/all/hooks';
import type { SearchRequestBody } from 'components/QuickViewSearch/shared/types';
import QuickView from 'components/quickView';
import { DefaultQuickViewHeader } from 'components/quickView/components/DefaultQuickViewComponents/Header';
import { breakpoint, sizes } from 'variables';
import { FindStoreResultContext, OnGeoLocationCallbackContext, OnSearchCallbackContext } from '../shared/helpers';
import { useFindInStoreQuickView } from '../shared/hooks';
import StoreListQuickView from '../storeListQuickview';

const StyledQuickView = styled(QuickView.Controlled)`
    ${breakpoint.up(sizes.md)} {
        min-width: initial;
    }
`;

export function FindInStoreProvider({
    productId,
    showClickAndCollectOnly,
    showClickAndCollectToggle,
    children
}: {
    productId: number;
    showClickAndCollectOnly: boolean;
    showClickAndCollectToggle?: boolean;
    children;
}) {
    const [findStoreResult, setFindStoreResult] = useState<FindStoreResultViewModel | undefined>(undefined);
    const [clickAndCollectToggle, setClickAndCollectToggle] = useState<boolean>(false);
    const [searchType, setSearchType] = useState<'address' | 'coordinates'>();
    const [addressQuery, setAddressQuery] = useState<string>();
    const [coordinates, setCoordinates] = useState<{ latitude: number; longitude: number }>({
        latitude: 0,
        longitude: 0
    });
    const [page, setPage] = useState<number>(1);
    const queryParams = { productId, onlyIfSundays: false, showOnlyClickAndCollect: showClickAndCollectOnly, page };

    function handleOnSuccess(data) {
        setFindStoreResult(data);
        showClickAndCollectToggle && setClickAndCollectToggle(!!data?.clickAndCollectToggleLabel);
    }

    const { refetch: searchStoreByAddress, isFetching: isLoadingSearchByAddress } =
        useSearchStoreForProductByAddressQuery(
            {
                addressQuery,
                ...queryParams
            },
            {
                onSuccess: (fetchedData) => {
                    handleOnSuccess(fetchedData);
                },
                enabled: false
            }
        );

    const { refetch: searchStoreByCoordinates, isFetching: isLoadingSearchByCoordinates } =
        useSearchStoreForProductByCoordinatesQuery(
            {
                latitude: coordinates?.latitude,
                longitude: coordinates?.longitude,
                ...queryParams
            },
            {
                onSuccess: (fetchedData) => {
                    handleOnSuccess(fetchedData);
                },
                enabled: false
            }
        );

    const isLoading = isLoadingSearchByAddress || isLoadingSearchByCoordinates;

    const onSearchCallback = useCallback(
        (data: SearchRequestBody): void => {
            const { searchQuery: addressQuery } = data;

            setAddressQuery(addressQuery);
            setSearchType('address');
        },
        [productId]
    );

    const onGeoLocationCallback = useCallback(
        (latitude: number, longitude: number): void => {
            setCoordinates({ latitude, longitude });
            setSearchType('coordinates');
        },
        [productId]
    );

    async function incrementPage() {
        setPage((currentPage) => {
            const nextPage = currentPage + 1;

            return nextPage;
        });
    }

    useEffect(() => {
        switch (searchType) {
            case 'address':
                searchStoreByAddress();
                break;
            case 'coordinates':
                searchStoreByCoordinates();
                break;
            default:
                break;
        }
    }, [showClickAndCollectOnly, addressQuery, coordinates, searchType, page]);

    return (
        <OnSearchCallbackContext.Provider value={onSearchCallback}>
            <OnGeoLocationCallbackContext.Provider value={onGeoLocationCallback}>
                <FindStoreResultContext.Provider value={findStoreResult?.findStoreResult}>
                    {children(findStoreResult, clickAndCollectToggle, incrementPage, isLoading)}
                </FindStoreResultContext.Provider>
            </OnGeoLocationCallbackContext.Provider>
        </OnSearchCallbackContext.Provider>
    );
}

export function useFindInStoreResult() {
    return useContext(FindStoreResultContext);
}

export default function FindInStoreQuickView() {
    const [
        {
            isOpen,
            productId,
            productInfo,
            clickAndCollectOnly,
            showClickAndCollectToggle,
            isClickAndCollect,
            showSelectParcelShop,
            isBasket,
            ...restProps
        },
        toggle
    ] = useFindInStoreQuickView();
    const [showClickAndCollectOnly, setShowClickAndCollectOnly] = useState<boolean>(!!clickAndCollectOnly);

    function closeQuickView() {
        toggle({ isOpen: false });
    }

    return (
        <StyledQuickView
            active={isOpen}
            onClose={closeQuickView}
            position="right"
            headerRenderer={() => <DefaultQuickViewHeader userCanClose onClose={closeQuickView} />}
            {...restProps}
        >
            {productId && (
                <FindInStoreProvider
                    productId={productId}
                    showClickAndCollectOnly={showClickAndCollectOnly}
                    showClickAndCollectToggle={showClickAndCollectToggle}
                >
                    {(
                        response: FindStoreResultViewModel | undefined,
                        showClickAndCollectToggle: boolean,
                        incrementPage: () => Promise<void>,
                        isLoading: boolean
                    ) =>
                        productInfo && (
                            <StoreListQuickView
                                findStoreResult={response?.findStoreResult}
                                numberOfStores={response?.numberOfStores}
                                productInfo={productInfo}
                                showSelectParcelShop={showSelectParcelShop}
                                showClickAndCollectToggle={showClickAndCollectToggle}
                                clickAndCollectLabel={response?.clickAndCollectToggleLabel}
                                clickAndCollectTimeTag={response?.clickAndCollectTimeTag}
                                showClickAndCollectOnly={showClickAndCollectOnly}
                                setShowClickAndCollectOnly={setShowClickAndCollectOnly}
                                isClickAndCollect={isClickAndCollect}
                                incrementPage={incrementPage}
                                isBasket={isBasket}
                                isLoading={isLoading}
                            />
                        )
                    }
                </FindInStoreProvider>
            )}
        </StyledQuickView>
    );
}
